import { useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'
import { castArray, isPlainObject, filter, intersection } from 'lodash-es'

import { useLoggedInUser } from '@/hooks'
import { batchClient } from '@/components/App/client'
import { Permission as PermissionType } from '@/types/Permission'

type Permission = {
  id: string
  info: string
  usesItemIds: boolean
  isSpecial?: boolean
}

const PERMISSIONS_QUERY = gql`
  query permissions {
    roles {
      nodes {
        id
        info
        usesItemIds
      }
    }
  }
`

const getBasePermissionName = (permissionName: string): string => {
  return permissionName.substring(0, permissionName.lastIndexOf(':'))
}
const getScope = (permissionName: string): string => {
  return permissionName.substring(permissionName.lastIndexOf(':') + 1)
}

const usePermissionCheck = () => {
  const { userId: loggedInUserId, userScope } = useLoggedInUser()

  const { data, loading } = useQuery(PERMISSIONS_QUERY, {
    client: batchClient,
  })

  const permissionNodes: Permission[] = useMemo(() => {
    return data?.roles?.nodes ?? []
  }, [data?.roles?.nodes])

  const getPermissionNode = (permission: string): Permission | undefined => {
    if (
      ['company_admin', 'superadmin', 'godmode'].includes(
        permission.toLowerCase(),
      )
    ) {
      return {
        id: permission,
        info: permission,
        usesItemIds: false,
        isSpecial: true,
      }
    }
    return permissionNodes?.find(
      node =>
        node.info === getBasePermissionName(permission) ||
        node.info ===
          getBasePermissionName(permission).replace(':read', ':write'),
    )
  }

  const hasPermissionInScope = (permission: string): boolean => {
    return Object.keys(userScope ?? {}).includes(permission)
  }

  const scopeIncludesItemId = (
    permission: PermissionType,
    departmentIds: (string | null)[] | undefined | null,
    userIds?: string[] | null,
    itemId?: string | null,
  ): boolean => {
    if (Array.isArray(userScope[permission])) {
      return userScope[permission].includes(itemId)
    } else if (isPlainObject(userScope[permission])) {
      const ignoreDepartmentIds =
        permission.endsWith(':department') && !departmentIds

      const ignoreUserIds = permission.endsWith(':self') && !userIds

      return (
        Object.keys(userScope).includes(permission) &&
        !!filter(userScope[permission], (value, key) => {
          return (
            (departmentIds?.includes(key) ||
              ignoreDepartmentIds ||
              userIds?.includes(key) ||
              ignoreUserIds) &&
            value?.includes(itemId)
          )
        }).length
      )
    }

    return false
  }

  const hasSelfPermission = (
    permission: PermissionType,
    userIds: string | string[] | undefined | null,
  ): boolean => {
    return (
      hasPermissionInScope(permission) &&
      (!userIds || castArray(userIds).includes(loggedInUserId))
    )
  }

  const hasDepartmentPermission = (
    permission: PermissionType,
    departmentIds?: (string | null)[] | null,
  ): boolean => {
    if (!departmentIds) {
      return hasPermissionInScope(permission)
    }

    if (Array.isArray(userScope[permission])) {
      return intersection(userScope[permission], departmentIds).length > 0
    } else if (isPlainObject(userScope[permission])) {
      return (
        intersection(Object.keys(userScope[permission]), departmentIds).length >
        0
      )
    }

    return false
  }

  const hasRelationPermissions = (
    permission: PermissionType,
    userIds: string[] | null,
  ): boolean => {
    if (!userIds) {
      return hasPermissionInScope(permission)
    }

    if (Array.isArray(userScope[permission])) {
      return intersection(userScope[permission], userIds).length > 0
    } else if (isPlainObject(userScope[permission])) {
      return (
        intersection(Object.keys(userScope[permission]), userIds).length > 0
      )
    }

    return false
  }

  const hasPermission = (
    permission: PermissionType,
    departmentIds?: (string | null)[] | null,
    userIds?: string | string[] | null,
    itemId?: string | null,
  ): boolean => {
    const permissionNode = getPermissionNode(permission)
    const permissionScope = getScope(permission)

    const requiresSelfPermission =
      !userIds !== null &&
      (userIds === loggedInUserId ||
        (Array.isArray(userIds) && userIds.includes(loggedInUserId)))

    const includesItemId: boolean =
      !permissionNode?.usesItemIds ||
      itemId === undefined ||
      scopeIncludesItemId(
        permission,
        departmentIds,
        userIds ? castArray(userIds) : null,
        itemId,
      )

    if (permissionScope === 'self') {
      return includesItemId && hasSelfPermission(permission, userIds)
    } else if (permissionScope === 'department') {
      if (!requiresSelfPermission) {
        return (
          includesItemId && hasDepartmentPermission(permission, departmentIds)
        )
      }
    } else if (permissionScope === 'relation') {
      return (
        includesItemId &&
        hasRelationPermissions(permission, userIds ? castArray(userIds) : null)
      )
    } else if (permissionScope === 'all' || permissionNode?.isSpecial) {
      if (!requiresSelfPermission) {
        return includesItemId && hasPermissionInScope(permission)
      }
    }

    return false
  }

  return {
    hasPermissions: (
      permissions?: PermissionType[],
      departmentIds?: (string | null)[] | null,
      userIds?: string | string[] | null,
      itemId?: string | null,
    ): boolean => {
      if (loading) {
        return false
      }

      if (!permissions || permissions.length === 0) {
        return true
      }

      return permissions.some(permission =>
        hasPermission(permission, departmentIds, userIds, itemId),
      )
    },
    loading,
  }
}

export default usePermissionCheck
